import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { Modal, ProgressBar } from '../ui';

/** Where a Post Dues / Post Late Fees run is: posting chunks, finished, or failed. */
export type PostRunState =
	| { phase: 'posting'; done: number; total: number }
	| { phase: 'done'; message: string; invoiceIds: string[]; printBlocked?: boolean }
	| { phase: 'failed'; message: string };

interface Props {
	/** `null` = no run in progress; the screen is hidden. */
	run: PostRunState | null;
	/** "dues" / "late fees", for the headings. */
	noun: string;
	/** Dues runs create invoices, so a finished run offers Print now / Print later. */
	offersPrint: boolean;
	onPrintNow: (invoiceIds: string[]) => void;
	onPrintLater: () => void;
	/** Finished with nothing to print, or failed. */
	onClose: () => void;
}

/**
 * Full-screen interstitial for a posting run. Posting takes a while (chunked POSTs, see
 * runChunkedCommit), so the page is covered with a progress bar until it is done — it cannot be
 * dismissed mid-run. Printing is offered only AFTERWARDS: the print click is then a fresh user
 * gesture, so its tab opens normally instead of a blank tab reserved before the post.
 */
export const PostProgressScreen: React.FC<Props> = ({ run, noun, offersPrint, onPrintNow, onPrintLater, onClose }) => {
	if (!run) return null;
	return (
		<Modal visible onDismiss={run.phase === 'posting' ? undefined : onClose} contentContainerStyle={styles.card}>
			{run.phase === 'posting' && (
				<View testID="post-progress-posting">
					<Text style={styles.title}>Posting {noun}…</Text>
					<ProgressBar progress={run.total > 0 ? run.done / run.total : 0} style={styles.bar} />
					<View style={styles.row}>
						<ActivityIndicator size="small" />
						<Text style={styles.detail}>{run.done} of {run.total} accounts — keep this page open.</Text>
					</View>
				</View>
			)}
			{run.phase === 'done' && (
				<View testID="post-progress-done">
					<Text style={styles.title}>Posted</Text>
					<Text style={styles.detail}>{run.message}</Text>
					{run.printBlocked && (
						<Text testID="post-print-blocked" style={styles.blocked}>
							Your browser blocked the print tab. Allow pop-ups for this site, then press Print now
							again — the invoices are still in the print queue.
						</Text>
					)}
					{offersPrint && run.invoiceIds.length > 0 ? (
						<View style={styles.actions}>
							<Pressable testID="post-print-later" onPress={onPrintLater} style={[styles.btn, styles.btnSecondary]}>
								<Text style={styles.btnSecondaryText}>Print later</Text>
							</Pressable>
							<Pressable testID="post-print-now" onPress={() => onPrintNow(run.invoiceIds)} style={[styles.btn, styles.btnPrimary]}>
								<Text style={styles.btnPrimaryText}>
									Print now ({run.invoiceIds.length} invoice{run.invoiceIds.length === 1 ? '' : 's'})
								</Text>
							</Pressable>
						</View>
					) : (
						<View style={styles.actions}>
							<Pressable testID="post-progress-close" onPress={onClose} style={[styles.btn, styles.btnPrimary]}>
								<Text style={styles.btnPrimaryText}>Done</Text>
							</Pressable>
						</View>
					)}
					{offersPrint && run.invoiceIds.length > 0 && (
						<Text style={styles.hint}>Print later keeps them in the print queue on the Invoices page.</Text>
					)}
				</View>
			)}
			{run.phase === 'failed' && (
				<View testID="post-progress-failed">
					<Text style={[styles.title, styles.failedTitle]}>Posting stopped</Text>
					<Text style={styles.detail}>{run.message}</Text>
					<View style={styles.actions}>
						<Pressable testID="post-progress-close" onPress={onClose} style={[styles.btn, styles.btnPrimary]}>
							<Text style={styles.btnPrimaryText}>Close</Text>
						</Pressable>
					</View>
				</View>
			)}
		</Modal>
	);
};

const styles = StyleSheet.create({
	card: { width: 480, maxWidth: '92%', padding: 24 },
	title: { fontSize: 18, fontWeight: '700', color: '#263238', marginBottom: 12 },
	failedTitle: { color: '#c62828' },
	bar: { height: 8, borderRadius: 4, marginBottom: 12 },
	row: { flexDirection: 'row', alignItems: 'center', gap: 8 },
	detail: { fontSize: 14, color: '#455a64', lineHeight: 20 },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
	btn: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 6 },
	btnPrimary: { backgroundColor: '#1976d2' },
	btnPrimaryText: { color: '#fff', fontWeight: '600' },
	btnSecondary: { backgroundColor: '#eceff1' },
	btnSecondaryText: { color: '#37474f', fontWeight: '600' },
	hint: { fontSize: 12, color: '#78909c', marginTop: 10, textAlign: 'right' },
	blocked: { fontSize: 13, color: '#b71c1c', marginTop: 12, lineHeight: 18 },
});
